/**
 * EVERY TAP VIBRATES — one delegated listener per app, installed at boot.
 *
 * WHY a delegate and not `hapticTap()` in each onClick: legacy got its "every button is felt"
 * by calling it by hand in 150 files, and the student app (9 files) proved that a rule nobody
 * can see is a rule nobody follows. One listener covers every control, including the ones
 * written tomorrow. Opt out per subtree with `data-haptic="off"`.
 *
 * WHY `click` and not `pointerdown`: a touch `pointerdown` is not a user activation, so
 * Android's `navigator.vibrate()` is ignored on the first tap of a page, and the iOS 18 switch
 * trick needs a real activation to fire. `click` is one on every platform.
 *
 * WHY capture phase: a component that calls `stopPropagation()` would otherwise go silent.
 * A component that also calls `hapticTap()` itself does not double-buzz: `haptic()` swallows a
 * second light tick inside the same tap (tap-dedupe.ts).
 *
 * WHY the engine loads lazily: importing haptic.ts (+ its audio synth) at boot put ~3 KB gz on
 * the legacy entry chunk, over its frozen budget (check-bundle). The listener is tiny and is
 * installed at boot; the engine arrives on the first touch or on idle (1.5 s cap), whichever
 * comes first. WHY the touch trigger: idle can be delayed for seconds on a busy mid-range
 * Android, and the click that follows the first pointerdown arrives after the import settles.
 */
type Tap = () => void;

let tapImpl: Tap | null = null;

let engineRequested = false;

function loadEngine(): void {
    if (engineRequested) return;
    engineRequested = true;
    void import('./haptic').then((m) => { tapImpl = m.hapticTap; });
}

const TAPPABLE = [
    'button',
    'a[href]',
    'summary',
    'label',
    'select',
    'input[type="checkbox"]',
    'input[type="radio"]',
    'input[type="submit"]',
    'input[type="button"]',
    '[role="button"]',
    '[role="tab"]',
    '[role="menuitem"]',
    '[role="option"]',
    '[role="switch"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[data-haptic]',
].join(',');

/**
 * iOS gets its buzz from tap-haptics.ios.ts (a finger's own click on a switch), not from the engine: since
 * iOS 26.5 the engine's scripted switch is silent. That module loads only here, off every Android's path.
 */
function isIosTouchDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
    // iPadOS asks for the desktop site and says "Macintosh"; a Mac has no touch points.
    return /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}

function pickControl(target: Element): HTMLElement | null {
    const control = target.closest(TAPPABLE);
    return control instanceof HTMLElement && !isSilent(control) ? control : null;
}

function isSilent(el: Element): boolean {
    if (el.closest('[data-haptic="off"]')) return true;
    if ((el as HTMLButtonElement).disabled) return true;
    return el.getAttribute('aria-disabled') === 'true';
}

/** Installs the listener once; returns the uninstall (tests, HMR). */
export function installTapHaptics(root: Document = document, engine?: Tap): () => void {
    engineRequested = false;
    if (engine) {
        tapImpl = engine;
        engineRequested = true;
    } else if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(loadEngine, { timeout: 1500 });
    } else setTimeout(loadEngine, 1);

    const onFirstTouch = (): void => loadEngine();
    if (!engine) {
        root.addEventListener('pointerdown', onFirstTouch, { capture: true, once: true, passive: true });
        root.addEventListener('touchstart', onFirstTouch, { capture: true, once: true, passive: true });
    }

    const ios = isIosTouchDevice();
    let uninstallIos: (() => void) | null = null;
    let uninstalled = false;
    if (ios) {
        void import('./tap-haptics.ios').then((m) => {
            if (!uninstalled) uninstallIos = m.installIosTapOverlay(root, pickControl);
        });
    }

    const onClick = (event: MouseEvent): void => {
        // Programmatic clicks are not taps — and the iOS trigger itself is a programmatic
        // label.click(): without this guard every haptic would re-enter the listener.
        if (ios || !event.isTrusted || !tapImpl) return;
        const target = event.target instanceof Element ? event.target : null;
        const control = target?.closest(TAPPABLE);
        if (!control || isSilent(control)) return;
        tapImpl();
    };
    root.addEventListener('click', onClick, { capture: true });
    return () => {
        uninstalled = true;
        uninstallIos?.();
        root.removeEventListener('click', onClick, { capture: true });
        root.removeEventListener('pointerdown', onFirstTouch, { capture: true });
        root.removeEventListener('touchstart', onFirstTouch, { capture: true });
    };
}
